import { Alert, Box } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';

import {
  ActivationDocumentTypes,
  AllDocumentTypes,
  Flag,
  FlagPurposeType,
  InstallationPackagePhotoCategoryTitles,
  MilestoneType,
  QueueDocumentResponse,
  QueueStatus,
  documentTitles,
} from 'types';

import { ActionsList } from '../ActionsList';
import FormSection from '../Forms/FormSection';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';

const missingDocumentCategories = {
  installPhotoDocumentation: 'Install Photo Documentation',
  designPackageDocuments: 'Design Package Documents',
};

type ConditionallyApprovedDocsProps = {
  flaggedDocs: Flag[];
  queue: QueueDocumentResponse;
  showCheckboxes: boolean;
  checkboxCategories: AllDocumentTypes[];
  isPackageEditable?: boolean;
};

export const ConditionallyApprovedDocs = ({
  flaggedDocs,
  queue,
  showCheckboxes,
  checkboxCategories,
  isPackageEditable = true,
}: ConditionallyApprovedDocsProps) => {
  const { flagDrivenPackageReview = false } = useFlags();
  const { id: accountId = '' } = useParams<{
    id: string;
  }>();
  const flagsWithFlagType = flaggedDocs?.filter(
    (flag) => flag.flagType && flag.purposeType === FlagPurposeType.stipulation,
  );
  const flaggedMissingDocs = flaggedDocs
    ?.map((flag: Flag & { category?: string }) => {
      const missingDoc =
        !flag.flagType &&
        Object.values({
          ...documentTitles,
          installPhotoDocumentation: missingDocumentCategories.installPhotoDocumentation,
          designPackageDocuments: missingDocumentCategories.designPackageDocuments,
        }).includes(flag.category || '') &&
        !flag.resolved
          ? flag.category
          : Object.values(InstallationPackagePhotoCategoryTitles).includes(flag.dataKey || '') && !flag.resolved
            ? flag.dataKey
            : undefined;

      const documentType = flag.type || flag.documentType;
      return missingDoc
        ? {
            missingDoc: Object.values(missingDocumentCategories).includes(missingDoc)
              ? documentTitles[documentType as keyof typeof documentTitles] || missingDoc
              : documentType
                ? documentTitles[documentType as keyof typeof documentTitles]
                : flag.category
                  ? flag.category
                  : flag.dataKey,
            note: flag.note,
          }
        : undefined;
    })
    .filter((missingDoc) => missingDoc !== undefined);

  const allStipulationFlagsOfInstallPackageAreResolved = flaggedDocs
    ?.filter((flag: Flag) => flag.purposeType === FlagPurposeType.stipulation)
    .every((flag: Flag) => flag.resolved);

  const MissingDocsList = () => {
    return (
      <ul>
        {flaggedMissingDocs?.map(
          (flaggedItem: any, index: number) =>
            flaggedItem && (
              <li key={`${flaggedItem.missingDoc}-${index}`}>
                {flaggedItem.missingDoc}
                {flaggedItem.note ? `: ${flaggedItem.note}` : ''}
              </li>
            ),
        )}
      </ul>
    );
  };

  return (
    <FormSection
      title="Conditional Approval Information"
      description="Provide necessary documentation, files or notes that address conditional approval requirements"
    >
      <Box childGap="lg">
        <Alert
          variant="warning"
          hasIcon
          fontSize="xs"
          title={
            flaggedMissingDocs && !flagsWithFlagType
              ? 'The Installation Package for this project was conditionally approved. We need final proof of the following submitted along with the Activation Package for Milestone Approval:'
              : allStipulationFlagsOfInstallPackageAreResolved
                ? 'The Installation Package for this project was conditionally approved. The following issues have already been resolved:'
                : 'The Installation Package for this project was conditionally approved. These flags must be addressed prior to Activation Package Submission by uploading the required documents under their corresponding items below:'
          }
          message={flaggedMissingDocs ? <MissingDocsList /> : null}
        />
        {flagDrivenPackageReview ? (
          <Box gap="lg">
            <ActionsList
              milestoneType={MilestoneType.install}
              accountId={accountId}
              customSummaryLabel="Stipulation Flags From"
              nonContextFlags={flagsWithFlagType}
              showActionListFooter={false}
              queue={{
                ...queue,
                status: QueueStatus.conditionallyApproved,
              }}
            />
          </Box>
        ) : (
          <DocumentList
            documentTypeFilter={[ActivationDocumentTypes.conditionalApproval]}
            title="Conditional Approval Documents"
            showTypeLabelOnItems={false}
            baseCategory={ActivationDocumentTypes.conditionalApproval}
            showCheckboxes={showCheckboxes}
            checkboxCategories={checkboxCategories}
            isPackageEditable={isPackageEditable}
          />
        )}
      </Box>
    </FormSection>
  );
};
